// The desk's incoming-scans feed — shared by PhoneScanModal, ScanLinkPanel and
// the pairing consoles so every surface renders a scan the same way: kind icon
// + friendly label, mono value, symbology caption, relative time. Display only —
// gate (c): the feed never offers an action that could commit.
import { For, Show } from "solid-js";
import type { Component } from "solid-js";
import { EmptyState } from "@erp/ui/empty-state";
import { BookOpen, CreditCard, HelpCircle, ScanLine } from "lucide-solid";
import type { ScanKind } from "../../schemas/scan.js";
import type { ScanEvent } from "./bridge.js";

export interface ScanKindMeta {
  label: string;
  /** Tailwind text-color class for the icon chip. */
  tone: string;
  icon: Component<{ size?: number; class?: string; "aria-hidden"?: boolean | "true" | "false" }>;
}

export const scanKindMeta = (kind: ScanKind): ScanKindMeta =>
  kind === "card"
    ? { label: "Patron card", tone: "text-secondary", icon: CreditCard }
    : kind === "book"
      ? { label: "Book", tone: "text-primary", icon: BookOpen }
      : { label: "Unknown", tone: "text-base-subtle", icon: HelpCircle };

/** "just now" / "4m ago" / "2h ago" / locale date — the roster + feed timestamp. */
export function relTime(ts: number): string {
  const d = Date.now() - ts;
  if (d < 30_000) return "just now";
  if (d < 3_600_000) return `${Math.max(1, Math.floor(d / 60_000))}m ago`;
  if (d < 86_400_000) return `${Math.floor(d / 3_600_000)}h ago`;
  return new Date(ts).toLocaleDateString();
}

export function ScanFeed(props: {
  events: ScanEvent[];
  emptyTitle?: string;
  emptyDescription?: string;
  /** Scroll cap, e.g. "max-h-56" (default). */
  maxHeightClass?: string;
  idPrefix?: string;
}) {
  return (
    <Show
      when={props.events.length > 0}
      fallback={
        <EmptyState
          icon={<ScanLine size={22} />}
          title={props.emptyTitle ?? "No scans yet"}
          description={props.emptyDescription ?? "Scans from paired phones land here in real time."}
        />
      }
    >
      <ul class={`flex flex-col gap-1.5 overflow-y-auto ${props.maxHeightClass ?? "max-h-56"}`}>
        <For each={props.events}>
          {(ev, i) => {
            const meta = scanKindMeta(ev.kind);
            return (
              <li
                id={props.idPrefix ? `${props.idPrefix}-${i()}` : undefined}
                class="flex items-center gap-3 rounded-field border border-base-300 px-3 py-2"
              >
                <meta.icon size={16} class={`shrink-0 ${meta.tone}`} aria-hidden="true" />
                <div class="min-w-0 flex-1">
                  <div class="truncate font-mono text-sm">{ev.value}</div>
                  <div class="text-xs text-base-subtle">
                    {meta.label}
                    {ev.format ? ` · ${ev.format}` : ""} · {relTime(ev.t)}
                  </div>
                </div>
              </li>
            );
          }}
        </For>
      </ul>
    </Show>
  );
}
